/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { memo, useEffect, useRef, useState } from "react";
import styles from "@src/components/organisms/searchbar-component/searchbar-component.module.scss";
import searchIcon from "@src/assets/svg/search.svg";
import clearIcon from "@src/assets/svg/close-icon.svg";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { useI18N } from "@src/language/language-context";

// It only emits search metadata (terms + active state). The actual filtering is
// done in use-alarm-list-new-hook.tsx so alarm list search runs in one place.

const cssClassName = "searchbar";

type SearchMeta = {
  isActive: boolean;
  searchTerms: string[];
};

type Props = {
  onSearchMetaChange: (meta: SearchMeta) => void;
  initialSearchTerms?: string[];
  initialSearchQuery?: string;
  showShortcut?: boolean;
};

export const AlarmSearchbarComponent = memo(
  ({
    onSearchMetaChange,
    initialSearchTerms = [],
    initialSearchQuery = "",
    showShortcut = true,
  }: Props) => {
    const i18n = useI18N();
    const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery);
    const [searchTerms, setSearchTerms] =
      useState<string[]>(initialSearchTerms);
    const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const focusInput = () =>
      requestAnimationFrame(() => inputRef.current?.focus());

    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isMac = /Mac|iPhone|iPad/.test(userAgent);
    const shortcutText = isMac ? "Cmd + F" : "Ctrl + F";

    useEffect(() => {
      if (!showShortcut) return undefined;
      const handleShortcut = (event: KeyboardEvent) => {
        if (
          (event.ctrlKey || event.metaKey) &&
          event.key.toLowerCase() === "f"
        ) {
          event.preventDefault();
          setIsSearchExpanded(true);
          focusInput();
        }
      };

      window.addEventListener("keydown", handleShortcut);
      return () => window.removeEventListener("keydown", handleShortcut);
    }, [showShortcut]);

    const handleAddTerm = (value: string) => {
      const term = value.trim();
      if (!term) return;
      const normalizedTerm = term.toLowerCase();
      const hasDuplicate = searchTerms.some(
        (existing) => existing.toLowerCase() === normalizedTerm,
      );
      if (hasDuplicate) {
        setSearchQuery("");
        return;
      }
      const nextTerms = [...searchTerms, term];
      setSearchTerms(nextTerms);
      setSearchQuery("");
      onSearchMetaChange({
        searchTerms: nextTerms,
        isActive: nextTerms.length > 0,
      });
    };

    const handleRemoveTerm = (term: string) => {
      const nextTerms = searchTerms.filter((t) => t !== term);
      setSearchTerms(nextTerms);
      onSearchMetaChange({
        searchTerms: nextTerms,
        isActive: nextTerms.length > 0,
      });
    };

    const handleClearSearch = () => {
      setSearchQuery("");
      setSearchTerms([]);
      onSearchMetaChange({
        searchTerms: [],
        isActive: false,
      });
      focusInput();
    };

    const handleBlur = () => {
      if (!searchQuery.trim() && searchTerms.length === 0) {
        setIsSearchExpanded(false);
      }
    };

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleAddTerm(searchQuery);
      }

      if (
        event.key === "Backspace" &&
        searchQuery.trim().length === 0 &&
        searchTerms.length > 0
      ) {
        handleRemoveTerm(searchTerms[searchTerms.length - 1]);
      }
    };

    const handleSearchClick = () => {
      handleAddTerm(searchQuery);
      focusInput();
    };
    const handleExpandAndFocus = () => {
      setIsSearchExpanded(true);
      focusInput();
    };

    const showSearchInput =
      isSearchExpanded ||
      searchQuery.trim().length > 0 ||
      searchTerms.length > 0;

    const titleText = i18n("modules.alarmList.searchForNodes");

    return (
      <div className={styles[cssClassName]}>
        <div
          className={styles[`${cssClassName}__search-wrapper`]}
          onClick={handleExpandAndFocus}
        >
          {!showSearchInput ? (
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleExpandAndFocus();
                }
              }}
              className={styles[`${cssClassName}__placeholder-text`]}
            >
              <img
                src={searchIcon}
                alt={titleText}
                className={styles[`${cssClassName}__search-icon`]}
              />
              <span>{titleText}</span>
              {showShortcut && (
                <strong className={styles[`${cssClassName}__shortcut-badge`]}>
                  {shortcutText}
                </strong>
              )}
            </div>
          ) : (
            <div className={styles[`${cssClassName}__search-input-wrapper`]}>
              <button
                type="button"
                className={styles[`${cssClassName}__search-trigger`]}
                onClick={handleSearchClick}
              >
                <img
                  src={searchIcon}
                  alt={titleText}
                  className={styles[`${cssClassName}__search-icon`]}
                />
              </button>

              <div className={styles[`${cssClassName}__tokens`]}>
                {searchTerms.map((term) => (
                  <span key={term} className={styles[`${cssClassName}__token`]}>
                    {term}
                    <VerticalSeparator />
                    <button
                      type="button"
                      className={styles[`${cssClassName}__token-remove`]}
                      onClick={() => handleRemoveTerm(term)}
                      aria-label={`Remove ${term}`}
                    >
                      <img
                        src={clearIcon}
                        alt={titleText}
                        className={styles[`${cssClassName}__token-remove-icon`]}
                      />
                    </button>
                  </span>
                ))}

                <input
                  ref={inputRef}
                  type="text"
                  className={styles[`${cssClassName}__search-input`]}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleBlur}
                  aria-label={titleText}
                />
              </div>

              {(searchQuery.trim().length > 0 || searchTerms.length > 0) && (
                <button
                  type="button"
                  className={styles[`${cssClassName}__clear-btn`]}
                  onClick={handleClearSearch}
                >
                  <img
                    src={clearIcon}
                    alt={titleText}
                    className={styles[`${cssClassName}__clear-icon`]}
                  />
                </button>
              )}

              {showShortcut && (
                <strong className={styles[`${cssClassName}__shortcut-badge`]}>
                  {shortcutText}
                </strong>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

AlarmSearchbarComponent.displayName = "AlarmSearchbarComponent";
